<template>
  <!-- One row per constat. Used by the property tab (property-id set) and
       the global "Liste des travaux" page (address columns + filters). -->
  <div>
    <d-data-table
      ref="data-table"
      resource="property-inspection-elements"
      :url="$laravel.route('api.property-inspection-elements.index')"
      fluid
      :no-padding="noPadding"
      :searchable="searchable"
      :scrollable="true"
      :parameters="parameters"
      default-sort-field="date"
      default-sort-order="desc"
      :edit-button="false"
      :delete-button="false"
    >
      <template
        v-if="showFilters"
        #slide-filters
      >
        <div class="works-filters">
          <b-select
            v-model="parameters.filter.status"
            @input="setFilter('status', $event)"
          >
            <option value="all">
              {{ $t("work_status_label") }} : {{ $t("all").trim() }}
            </option>
            <option
              v-for="option in $laravel.propertyInspectionElementStatuses"
              :key="option"
              :value="option"
            >
              {{ $t(option).capitalize() }}
            </option>
          </b-select>
          <b-select
            v-model="parameters.filter.type"
            @input="setFilter('type', $event)"
          >
            <option value="all">
              {{ $t("type") }} : {{ $t("all").trim() }}
            </option>
            <option
              v-for="option in $laravel.propertyInspectionElementTypes"
              :key="option"
              :value="option"
            >
              {{ $t(option).capitalize() }}
            </option>
          </b-select>
          <b-select
            v-model="parameters.filter.property_status"
            @input="setFilter('property_status', $event)"
          >
            <option value="all">
              {{ $t("property_status_label") }} : {{ $t("all").trim() }}
            </option>
            <option
              v-for="status in $laravel.propertyStatuses"
              :key="status"
              :value="status"
            >
              {{ $t(status).capitalize() }}
            </option>
          </b-select>
          <b-select
            v-model="parameters.filter.occupation"
            @input="setFilter('occupation', $event)"
          >
            <option value="all">
              {{ $t("occupation_label") }} : {{ $t("all").trim() }}
            </option>
            <option value="vacant">
              {{ $t("not_occupied") }}
            </option>
            <option value="occupied">
              {{ $t("occupied") }}
            </option>
          </b-select>
        </div>
      </template>

      <b-table-column
        v-slot="props"
        field="date"
        :label="$t('date').capitalize()"
        sortable
      >
        <span class="is-size-7 no-wrap-text">{{ $shortDate(props.row.date, false) }}</span>
      </b-table-column>

      <b-table-column
        v-if="showProperty"
        v-slot="props"
        field="address"
        :label="$t('address').capitalize()"
        sortable
      >
        <router-link
          v-if="props.row.property"
          :to="`/properties/${props.row.property.id}/property-inspections`"
          class="has-text-info has-text-weight-semibold is-size-7 work-address"
          :title="props.row.property.full_address"
        >
          {{ shortStreet(props.row.property.address_1) }}
        </router-link>
      </b-table-column>

      <b-table-column
        v-if="showProperty"
        v-slot="props"
        field="address_2"
        :label="$t('address_2').capitalize()"
      >
        <span class="is-size-7">{{ props.row.property ? props.row.property.address_2 : "" }}</span>
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="status"
        :label="showProperty ? $t('work_status_label') : $t('status').capitalize()"
        sortable
      >
        <!-- Not disabled while saving: Buefy copies "is-disabled" onto the
             menu appended to the body and it stays half transparent
             afterwards; updateStatus() ignores clicks while busy instead -->
        <b-dropdown
          aria-role="list"
          append-to-body
        >
          <template #trigger>
            <!-- Compact: colour + label only, no status icon -->
            <button
              type="button"
              class="button is-small work-status"
              :class="`is-${props.row.status_color}`"
            >
              <span>{{ $t(props.row.status) }}</span>
              <span class="icon is-small"><i class="mdi mdi-chevron-down" /></span>
            </button>
          </template>
          <b-dropdown-item
            v-for="option in $laravel.propertyInspectionElementStatuses"
            :key="option"
            aria-role="listitem"
            :value="option"
            @click="updateStatus(props.row, option)"
          >
            {{ $t(option).capitalize() }}
          </b-dropdown-item>
        </b-dropdown>
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="type"
        :label="$t('type')"
        sortable
      >
        <span class="tag is-light is-rounded is-size-7">{{ $t(props.row.type) }}</span>
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="notes"
        :label="$t('description').capitalize()"
      >
        <div
          v-if="hasNotes(props.row)"
          class="work-notes is-size-7"
        >
          <div
            v-if="expanded.includes(props.row.id)"
            class="content mb-1"
            v-html="props.row.notes"
          />
          <span
            v-else
            class="work-excerpt"
          >{{ excerpt(props.row) }}</span>
          <a
            v-if="isLong(props.row)"
            class="work-more"
            :class="{ 'is-block': expanded.includes(props.row.id) }"
            @click="toggleExpanded(props.row.id)"
          >
            {{ expanded.includes(props.row.id) ? $t("show_less") : $t("show_more") }}
          </a>
        </div>
        <span
          v-else
          class="has-text-grey"
        >-</span>
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="photos"
        :label="$t('photos').capitalize()"
      >
        <button
          type="button"
          class="button is-small"
          @click="openPhotos(props.row)"
        >
          <span class="icon"><i class="mdi mdi-image-multiple-outline" /></span>
          <span>{{ $t("see") }}<template v-if="props.row.media && props.row.media.length"> ({{ props.row.media.length }})</template></span>
        </button>
      </b-table-column>

      <b-table-column
        v-if="showProperty"
        v-slot="props"
        field="property_status"
        :label="$t('property_status_label')"
      >
        <span
          v-if="props.row.property && props.row.property.status_type"
          class="status-pill"
          :class="propertyStatusClass(props.row.property)"
        >
          {{ propertyStatusLabel(props.row.property) }}
        </span>
      </b-table-column>

      <b-table-column
        v-if="showProperty"
        v-slot="props"
        field="occupation"
        :label="$t('occupation_label')"
      >
        <span
          v-if="props.row.property && props.row.property.status_type"
          class="status-pill"
          :class="props.row.property.is_occupied ? 'is-occupied' : 'is-vacant'"
        >
          {{ props.row.property.is_occupied ? $t("occupied") : $t("not_occupied") }}
        </span>
      </b-table-column>

      <b-table-column
        v-if="!showProperty"
        v-slot="props"
        field="user"
        :label="$t('user').capitalize()"
      >
        <b-tooltip
          v-if="props.row.user_label"
          type="is-black"
          position="is-left"
          :label="props.row.user ? props.row.user.name : ''"
        >
          <span
            class="tag is-size-7 user-tag"
            :class="props.row.user_label === 'Devloc' ? 'is-devloc' : 'is-client'"
          >{{ props.row.user_label }}</span>
        </b-tooltip>
        <span
          v-else
          class="has-text-grey"
        >-</span>
      </b-table-column>

      <template #actions="{ row }">
        <!-- Approve: green check when approved, grey check to approve -->
        <b-tooltip
          type="is-black"
          position="is-left"
          :label="row.approved_by_client ? approvedLabel(row) : $t('approve')"
        >
          <button
            type="button"
            class="button is-small is-white work-approve"
            :class="{ 'is-approved': row.approved_by_client }"
            :disabled="row.approved_by_client || !(row.can && row.can.update) || busy"
            @click="approve(row)"
          >
            <span class="icon"><i class="mdi mdi-check-circle mdi-20px" /></span>
          </button>
        </b-tooltip>
        <button
          v-if="row.can && row.can.update"
          type="button"
          class="button is-small is-white"
          :title="$t('edit')"
          @click="openEdit(row)"
        >
          <span class="icon has-text-grey"><i class="mdi mdi-pencil mdi-18px" /></span>
        </button>
        <button
          v-if="row.can && row.can.delete"
          type="button"
          class="button is-small is-white"
          :title="$t('delete').capitalize()"
          @click="remove(row)"
        >
          <span class="icon has-text-grey"><i class="mdi mdi-close mdi-18px" /></span>
        </button>
      </template>
    </d-data-table>

    <v-constat-form
      :active="isFormOpen"
      :property-id="formPropertyId"
      :element="editing"
      @close="isFormOpen = false"
      @saved="reload"
    />
    <v-element-photos
      :active="isPhotosOpen"
      :element="photosElement"
      @close="isPhotosOpen = false"
      @changed="reloadPhotos"
    />
  </div>
</template>

<script>
import notificator from "@/services/notificator";
import DataTableFilters from "@/shared/mixins/DataTableFilters";
import ConstatFormModal from "./ConstatFormModal.vue";
import ElementPhotosModal from "./ElementPhotosModal.vue";

export default {
  components: {
    "v-constat-form": ConstatFormModal,
    "v-element-photos": ElementPhotosModal,
  },
  mixins: [DataTableFilters],
  props: {
    propertyId: {
      type: Number,
      default: null,
    },
    showProperty: {
      type: Boolean,
      default: false,
    },
    showFilters: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filterKey: this.propertyId ? "works_" + this.propertyId : "works",
      busy: false,
      expanded: [],
      isFormOpen: false,
      editing: null,
      formPropertyId: this.propertyId,
      isPhotosOpen: false,
      photosElement: null,
      dateFilter: [],
      parameters: {
        filter: {
          property_id: this.propertyId,
          status: "all",
          type: "all",
          property_status: "all",
          occupation: "all",
          start_date: null,
          end_date: null,
        },
      },
    };
  },
  created() {
    if (!this.showFilters) {
      return;
    }
    // Restore the saved filters (same mechanism as the other lists)
    this.getFilters().forEach((filter) => {
      if (filter.key in this.parameters.filter) {
        this.parameters.filter[filter.key] = filter.value;
      }
    });
  },
  methods: {
    reload() {
      this.$refs["data-table"].loadData();
    },
    // Keep the photos popup in sync after an add / delete
    reloadPhotos() {
      const id = this.photosElement && this.photosElement.id;

      this.$http
        .get(this.$laravel.route("api.property-inspection-elements.index"), {
          params: { filter: { property_id: this.formPropertyId }, per_page: 200 },
        })
        .then((response) => {
          const fresh = (response.data.data || []).find((row) => row.id === id);

          if (fresh) {
            this.photosElement = fresh;
          }
        })
        .finally(() => this.reload());
    },
    openCreate(propertyId = null) {
      this.formPropertyId = propertyId || this.propertyId;
      this.editing = null;
      this.isFormOpen = true;
    },
    openEdit(row) {
      this.formPropertyId = row.property ? row.property.id : this.propertyId;
      this.editing = row;
      this.isFormOpen = true;
    },
    openPhotos(row) {
      this.formPropertyId = row.property ? row.property.id : this.propertyId;
      this.photosElement = row;
      this.isPhotosOpen = true;
    },
    setDateFilter(range) {
      const start = range && range[0] ? this.$dayjs(range[0]).format("YYYY-MM-DD") : null;
      const end = range && range[1] ? this.$dayjs(range[1]).format("YYYY-MM-DD") : null;

      this.parameters.filter.start_date = start;
      this.parameters.filter.end_date = end;
      this.setFilter("start_date", start);
      this.setFilter("end_date", end);
    },
    resetDateFilter() {
      this.dateFilter = [];
      this.setDateFilter([]);
    },
    // "Boulevard Pie-IX" -> "Boul. Pie-IX": the address column stays narrow
    shortStreet(address) {
      return (address || "")
        .replace(/\bAvenue\b/i, "Av.")
        .replace(/\bBoulevard\b/i, "Boul.")
        .replace(/\bChemin\b/i, "Ch.");
    },
    plainNotes(row) {
      return (row.notes || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    },
    hasNotes(row) {
      return this.plainNotes(row) !== "";
    },
    // One line on phones (the card shows the label beside the text)
    excerptLength() {
      return this.$mq === "mobile" ? 34 : 110;
    },
    isLong(row) {
      return this.plainNotes(row).length > this.excerptLength();
    },
    excerpt(row) {
      const text = this.plainNotes(row);
      const max = this.excerptLength();

      return text.length > max ? text.slice(0, max).trim() + "…" : text;
    },
    toggleExpanded(id) {
      const index = this.expanded.indexOf(id);

      if (index === -1) {
        this.expanded.push(id);
      } else {
        this.expanded.splice(index, 1);
      }
    },
    approvedLabel(row) {
      return this.$t("approved") + (row.approval_date ? " — " + this.$shortDate(row.approval_date, false) : "");
    },
    propertyStatusClass(property) {
      if (property.status_type === "in_renovation") {
        return property.in_renovation_status === "pending" ? "is-pending" : "is-in-renovation";
      }

      return { for_rent: "is-for-rent", in_application: "is-in-application", rented: "is-rented" }[property.status_type] || "";
    },
    propertyStatusLabel(property) {
      if (property.status_type === "in_renovation") {
        return this.$t(property.in_renovation_status === "pending" ? "status_short_pending" : "status_short_in_renovation");
      }

      return this.$t("status_short_" + property.status_type);
    },
    updateStatus(row, status) {
      if (!status || status === row.status || this.busy) {
        return;
      }
      this.busy = true;
      this.$http
        .patch(
          this.$laravel.route("api.property-inspections.update_element_status", {
            propertyInspectionElement: row.id,
          }),
          { status }
        )
        .then(() => this.reload())
        .catch((e) => console.error(e))
        .finally(() => {
          this.busy = false;
        });
    },
    approve(row) {
      if (row.approved_by_client || this.busy) {
        return;
      }
      this.$buefy.dialog.confirm({
        title: this.$t("confirm_approve_property_inspection_title"),
        message: this.$t("confirm_approve_property_inspection_msg"),
        confirmText: this.$t("confirm"),
        cancelText: this.$t("cancel"),
        type: "is-success",
        onConfirm: () => {
          this.busy = true;
          this.$http
            .patch(
              this.$laravel.route("api.property-inspection-elements.approve", {
                propertyInspectionElement: row.id,
              })
            )
            .then((response) => {
              notificator.open({ message: response.data.message, type: "success" });
              this.reload();
            })
            .catch((e) => console.error(e))
            .finally(() => {
              this.busy = false;
            });
        },
      });
    },
    remove(row) {
      this.$buefy.dialog.confirm({
        title: this.$t("delete_record"),
        message: this.$t("confirm_delete"),
        confirmText: this.$t("confirm"),
        cancelText: this.$t("cancel"),
        type: "is-danger",
        onConfirm: () => {
          this.busy = true;
          this.$http
            .delete(
              this.$laravel.route("api.property-inspection-elements.destroy", {
                propertyInspectionElement: row.id,
              })
            )
            .then((response) => {
              notificator.open({ message: response.data.message, type: "success" });
              this.reload();
            })
            .catch((e) => console.error(e))
            .finally(() => {
              this.busy = false;
            });
        },
      });
    },
  },
};
</script>

<style scoped>
.no-wrap-text {
  white-space: nowrap;
}
/* Same height and text size as the Type tag beside it */
.work-status {
  min-width: 104px;
  height: 26px;
  padding: 0 5px 0 9px;
  font-size: 12px;
  line-height: 1;
  border-radius: 6px;
  justify-content: space-between;
}
.work-status .icon.is-small {
  height: 16px;
  width: 16px;
}
.work-notes {
  max-width: 260px;
  min-width: 200px; /* wraps at a readable width instead of a tall sliver */
  overflow-wrap: anywhere; /* unbroken strings wrap instead of overflowing */
  word-break: break-word;
}
.work-notes .content {
  overflow-wrap: anywhere;
  word-break: break-word;
}
.work-address {
  white-space: nowrap;
}
.work-more {
  display: inline-block;
  margin-left: 4px;
  font-weight: 600;
  white-space: nowrap;
}
.work-more.is-block {
  display: block;
  margin-left: 0;
}
.work-approve .mdi {
  color: #b5b5b5;
}
.work-approve.is-approved .mdi {
  color: #2f8a5a;
}
.work-approve:disabled {
  opacity: 1;
}
.user-tag.is-devloc {
  background: #e7edf6;
  color: #2e3d6e;
}
.user-tag.is-client {
  background: #f1eee7;
  color: #4a4a4a;
}
.works-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
@media screen and (max-width: 768px) {
  .works-filters {
    flex-direction: column;
    align-items: stretch;
  }
  .works-filters > * {
    width: 100%;
  }
  .work-notes {
    max-width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }
  .work-excerpt {
    display: block;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .work-more {
    margin-left: 0;
  }
  .work-notes .content {
    text-align: left;
    max-width: 100%;
  }
}
</style>
